// Copyright 2026. WebPros International GmbH. All rights reserved.

import {
    IPaginateApiResponse,
    paginateInitialState,
} from 'common/api/resources/Response';
import {
    IHighAvailabilityActivity,
    HighAvailabilityStatus,
    HighAvailabilityEntityStatus,
} from 'common/api/resources/HighAvailability';
import {
    ITaskResponse,
    TASK_ACTION,
} from 'common/api/resources/Task';
import {
    ActionType,
    getType,
} from 'typesafe-actions';
import * as highAvailabilityActions from 'common/modules/highAvailability/actions';
import { combineReducers } from 'redux';
import {
    appendToPaginated,
    updateInPaginated,
} from 'common/reducers/list';

export interface IHighAvailabilityReducer {
    list: IPaginateApiResponse<IHighAvailabilityActivity[]>;
}

export type HaActions = ActionType<typeof highAvailabilityActions>;
export type HaState = Readonly<IHighAvailabilityReducer>;

export default combineReducers<HaState, HaActions>({
    list: (state = paginateInitialState, action) => {
        switch (action.type) {
        case getType(highAvailabilityActions.setHighAvailabilities):
            return action.payload;
        case getType(highAvailabilityActions.setHighAvailabilityEntitiesForActivity):
            return ({
                ...state,
                data: state.data.map(activity => {
                    if (action.payload.activityId === activity.id) {
                        return { ...activity, entities: action.payload.data };
                    }

                    return activity;
                }),
            });
        case getType(highAvailabilityActions.setHighAvailabilityLoading):
            return ({
                ...state,
                data: state.data.map(activity => {
                    if (action.payload === activity.id) {
                        return { ...activity, is_loading: true };
                    }

                    return activity;
                }),
            });
        case getType(highAvailabilityActions.unsetHighAvailabilityLoading):
            return ({
                ...state,
                data: state.data.map(activity => {
                    if (action.payload === activity.id) {
                        return { ...activity, is_loading: false };
                    }

                    return activity;
                }),
            });
        case getType(highAvailabilityActions.appendHighAvailabilityEntities):
            return ({
                ...state,
                data: state.data.map(activity => {
                    if (action.payload.activityId === activity.id && activity.entities) {
                        return { ...activity, entities: appendToPaginated(activity.entities, action.payload.data) };
                    }

                    return activity;
                }),
            });
        case getType(highAvailabilityActions.setHighAvailabilityEntityLoading):
            return ({
                ...state,
                data: state.data.map(activity => {
                    if (!activity.entities) {
                        return activity;
                    }

                    return {
                        ...activity,
                        entities: {
                            ...activity.entities,
                            data: activity.entities.data.map(entity => {
                                if (entity.id === action.payload) {
                                    return { ...entity, is_loading: true };
                                }

                                return entity;
                            }),
                        },
                    };
                }),
            });
        case getType(highAvailabilityActions.unsetHighAvailabilityEntityLoading):
            return ({
                ...state,
                data: state.data.map(activity => {
                    if (!activity.entities) {
                        return activity;
                    }

                    return {
                        ...activity,
                        entities: {
                            ...activity.entities,
                            data: activity.entities.data.map(entity => {
                                if (entity.id === action.payload) {
                                    return { ...entity, is_loading: false };
                                }

                                return entity;
                            }),
                        },
                    };
                }),
            });
        case getType(highAvailabilityActions.setHighAvailabilityEntityMoveBackPending):
            return ({
                ...state,
                data: state.data.map(activity => {
                    if (!activity.entities) {
                        return activity;
                    }

                    const byActivityId = action.payload.high_availability_activity_ids?.includes(activity.id);

                    return {
                        ...activity,
                        entities: {
                            ...activity.entities,
                            data: activity.entities.data.map(entity => {
                                const byEntityId = action.payload.entity_ids?.includes(entity.id);

                                if (byEntityId || byActivityId) {
                                    return { ...entity, is_move_back_pending: true };
                                }

                                return entity;
                            }),
                        },
                    };
                }),
            });
        case getType(highAvailabilityActions.unsetHighAvailabilityEntityMoveBackPending):
            return ({
                ...state,
                data: state.data.map(activity => {
                    if (!activity.entities) {
                        return activity;
                    }

                    const byActivityId = action.payload.high_availability_activity_ids?.includes(activity.id);

                    return {
                        ...activity,
                        entities: {
                            ...activity.entities,
                            data: activity.entities.data.map(entity => {
                                const byEntityId = action.payload.entity_ids?.includes(entity.id);

                                if (byEntityId || byActivityId) {
                                    return { ...entity, is_move_back_pending: false };
                                }

                                return entity;
                            }),
                        },
                    };
                }),
            });
        case getType(highAvailabilityActions.updateHighAvailabilityEntity):
            return ({
                ...state,
                data: state.data.map(activity => {
                    if (activity.id !== action.payload.entity.high_availability.id) {
                        return activity;
                    }

                    if (activity.entities) {
                        // updateInPaginated is a no-op if the broadcast entity is
                        // not in the loaded page — but the broadcast still arrived,
                        // so we must not drop it. We fall through to use the
                        // backend-authoritative status from the payload below.
                        const updatedEntities = updateInPaginated(
                            activity.entities,
                            action.payload.entity,
                            'id'
                        );
                        const allEntitiesLoaded =
                            updatedEntities.data.length >= activity.entities_count.total;
                        const updated = { ...activity, entities: updatedEntities };

                        return {
                            ...updated,
                            // Only recompute locally when we have the full set;
                            // otherwise trust the backend (Observer::saved →
                            // computeStatus runs over ALL entities). Recomputing
                            // over a partial page would falsely flip the badge.
                            status: allEntitiesLoaded
                                ? updateActivityStatus(updated)
                                : action.payload.entity.high_availability.status,
                        };
                    }

                    return {
                        ...activity,
                        status: action.payload.entity.high_availability.status,
                    };
                }),
            });
        case getType(highAvailabilityActions.updateHighAvailabilityEntityTask):
            return ({
                ...state,
                data: state.data.map(activity => {
                    if (activity.entities) {
                        return {
                            ...activity,
                            entities: {
                                ...activity.entities,
                                data: activity.entities.data.map(entity => {
                                    if (entity.task?.id === action.payload.taskId) {
                                        return {
                                            ...entity,
                                            task: {
                                                ...entity.task,
                                                progress: action.payload.progress,
                                                status: action.payload.status,
                                            },
                                        };
                                    }

                                    if (entity.move_back_task?.id === action.payload.taskId) {
                                        return {
                                            ...entity,
                                            move_back_task: {
                                                ...entity.move_back_task,
                                                progress: action.payload.progress,
                                                status: action.payload.status,
                                            },
                                        };
                                    }

                                    if (
                                        action.payload.action === TASK_ACTION.SERVER_MIGRATE &&
                                        action.payload.computeResourceVmId !== null &&
                                        action.payload.computeResourceId !== null &&
                                        entity.compute_resource_vm.id === action.payload.computeResourceVmId &&
                                        // The migration must be heading BACK to this activity's source CR.
                                        // Without this guard a plain admin migration of the same VM would
                                        // render as "moving back" and disable the row's real move-back action.
                                        action.payload.computeResourceId === activity.source_compute_resource.id
                                    ) {
                                        return {
                                            ...entity,
                                            move_back_task: {
                                                id: action.payload.taskId,
                                                progress: action.payload.progress,
                                                status: action.payload.status,
                                            } as ITaskResponse,
                                        };
                                    }

                                    return entity;
                                }),
                            },
                        };
                    }

                    return activity;
                }),
            });
        default:
            return state;
        }
    },
});

const updateActivityStatus = (activity: IHighAvailabilityActivity) => {
    if (activity.entities && activity.entities.data.length > 0) {
        const statuses = activity.entities.data.map(entity => entity.status);
        const hasRunning = statuses.includes(HighAvailabilityEntityStatus.RUNNING);
        const hasPending = statuses.includes(HighAvailabilityEntityStatus.PENDING);
        const hasFailed = statuses.includes(HighAvailabilityEntityStatus.FAILED);
        const hasCompleted = statuses.includes(HighAvailabilityEntityStatus.COMPLETED);
        const hasCancelled = statuses.includes(HighAvailabilityEntityStatus.CANCELLED);

        if ((hasFailed || hasCancelled) && (hasRunning || hasPending)) {
            return HighAvailabilityStatus.RUNNING_WITH_ERRORS;
        }

        if (hasRunning) {
            return HighAvailabilityStatus.RUNNING;
        }

        if (hasFailed && hasCompleted) {
            return HighAvailabilityStatus.COMPLETED_WITH_ERRORS;
        }

        if (hasFailed) {
            return HighAvailabilityStatus.FAILED;
        }

        if (hasCancelled && hasCompleted) {
            return HighAvailabilityStatus.COMPLETED_WITH_ERRORS;
        }

        if (hasCancelled) {
            return HighAvailabilityStatus.CANCELLED;
        }

        if (hasPending) {
            return HighAvailabilityStatus.PENDING;
        }

        return HighAvailabilityStatus.COMPLETED;
    }

    return activity.status;
};
